import React, { useState, useEffect, useReducer, useContext } from "react";

import { makeStyles } from "@material-ui/core/styles";
import List from "@material-ui/core/List";
import Paper from "@material-ui/core/Paper";

import TicketListItem from "../TicketListItemCustom";
import TicketsListSkeleton from "../TicketsListSkeleton";

import useTickets from "../../hooks/useTickets";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import { SocketContext } from "../../context/Socket/SocketContext";

const useStyles = makeStyles((theme) => ({
  ticketsListWrapper: {
    position: "relative",
    display: "flex",
    height: "100%",
    flexDirection: "column",
    overflow: "hidden",
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
  },

  ticketsList: {
    flex: 1,
    maxHeight: "100%",
    overflowY: "scroll",
    ...theme.scrollbarStyles,
    borderTop: "2px solid rgba(0, 0, 0, 0.12)",
  },

  ticketsListHeader: {
    color: "rgb(67, 83, 105)",
    zIndex: 2,
    backgroundColor: "white",
    borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },

  ticketsCount: {
    fontWeight: "normal",
    color: "rgb(104, 121, 146)",
    marginLeft: "8px",
    fontSize: "14px",
  },

  noTicketsText: {
    textAlign: "center",
    color: "rgb(104, 121, 146)",
    fontSize: "14px",
    lineHeight: "1.4",
  },

  noTicketsTitle: {
    textAlign: "center",
    fontSize: "16px",
    fontWeight: "600",
    margin: "0px",
  },

  noTicketsDiv: {
    display: "flex",
    height: "100px",
    margin: 40,
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },
}));

const sortTicketsByLastMessage = (tickets) => {
  const getDateValue = (value) => {
    if (!value) return 0;
    const timestamp = new Date(value).getTime();
    return Number.isNaN(timestamp) ? 0 : timestamp;
  };

  return [...tickets].sort((a, b) => {
    const sortAtDiff =
      getDateValue(b.lastMessageSortAt) - getDateValue(a.lastMessageSortAt);
    if (sortAtDiff !== 0) {
      return sortAtDiff;
    }

    const updatedAtDiff = getDateValue(b.updatedAt) - getDateValue(a.updatedAt);
    if (updatedAtDiff !== 0) {
      return updatedAtDiff;
    }

    return Number(b.id) - Number(a.id);
  });
};

const reducer = (state, action) => {
  if (action.type === "LOAD_TICKETS") {
    const newTickets = action.payload;

    newTickets.forEach((ticket) => {
      const ticketIndex = state.findIndex((t) => t.id === ticket.id);
      if (ticketIndex !== -1) {
        state[ticketIndex] = ticket;
      } else {
        state.push(ticket);
      }
    });

    return sortTicketsByLastMessage(state);
  }

  if (action.type === "RESET_UNREAD") {
    const ticketId = action.payload;

    const ticketIndex = state.findIndex((t) => t.id === ticketId);
    if (ticketIndex !== -1) {
      state[ticketIndex].unreadMessagesForUser = 0;
    }

    return [...state];
  }

  if (action.type === "UPDATE_TICKET") {
    const ticket = action.payload;

    const ticketIndex = state.findIndex((t) => t.id === ticket.id);
    if (ticketIndex !== -1) {
      if (ticket.unreadMessagesForUser === undefined) {
        ticket.unreadMessagesForUser = state[ticketIndex].unreadMessagesForUser;
      }
      state[ticketIndex] = ticket;
    } else {
      state.push(ticket);
    }

    return sortTicketsByLastMessage(state);
  }

  if (action.type === "UPDATE_TICKET_UNREAD_MESSAGES") {
    const ticket = action.payload;

    const ticketIndex = state.findIndex((t) => t.id === ticket.id);
    if (ticketIndex !== -1) {
      if (ticket.unreadMessagesForUser === undefined) {
        ticket.unreadMessagesForUser = state[ticketIndex].unreadMessagesForUser;
      }
      state[ticketIndex] = ticket;
    } else {
      state.push(ticket);
    }

    return sortTicketsByLastMessage(state);
  }

  if (action.type === "UPDATE_TICKET_CONTACT") {
    const contact = action.payload;
    const ticketIndex = state.findIndex((t) => t.contactId === contact.id);
    if (ticketIndex !== -1) {
      state[ticketIndex].contact = contact;
    }
    return [...state];
  }

  if (action.type === "DELETE_TICKET") {
    const ticketId = action.payload;
    const ticketIndex = state.findIndex((t) => t.id === ticketId);
    if (ticketIndex !== -1) {
      state.splice(ticketIndex, 1);
    }

    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const TicketsListCustom = (props) => {
  const {
    status,
    searchParam,
    tags,
    users,
    contactId,
    showAll,
    selectedQueueIds,
    updateCount,
    style,
    isGroup,
  } = props;
  const classes = useStyles();
  const [pageNumber, setPageNumber] = useState(1);
  const [ticketsList, dispatch] = useReducer(reducer, []);
  const { user } = useContext(AuthContext);
  const profile = user?.profile;
  const queues = Array.isArray(user?.queues) ? user.queues : [];

  const socketManager = useContext(SocketContext);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [
    status,
    searchParam,
    dispatch,
    showAll,
    tags,
    users,
    contactId,
    selectedQueueIds,
    isGroup,
  ]);

  const { tickets, hasMore, loading } = useTickets({
    pageNumber,
    searchParam,
    status,
    showAll,
    tags: JSON.stringify(tags),
    users: JSON.stringify(users),
    contactId,
    queueIds: JSON.stringify(selectedQueueIds),
    isGroup,
  });

  useEffect(() => {
    const queueIds = queues.map((q) => q.id);
    let resultTickets = tickets;

    if (!isGroup) {
      if (profile === "user") {
        resultTickets = tickets.filter((t) => queueIds.indexOf(t.queueId) > -1);
      }
    } else {
      if (profile === "user") {
        resultTickets = tickets.filter((t) =>
          t.contact?.groupUsers?.some(
            (gu) => Number(gu.userId) === Number(user?.id),
          ),
        );
      } else {
        resultTickets = tickets.filter((t) => {
          const groupUsers = t.contact?.groupUsers || [];
          return (
            groupUsers.length === 0 ||
            groupUsers.some((gu) => Number(gu.userId) === Number(user?.id))
          );
        });
      }
    }

    dispatch({ type: "LOAD_TICKETS", payload: resultTickets });
  }, [tickets, status, searchParam, queues, profile, isGroup, user?.id]);

  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    const socket = socketManager.getSocket(companyId);

    const matchesGroupFilter = (ticket) => {
      if (isGroup === true) {
        return ticket?.isGroup === true;
      }

      if (isGroup === false) {
        return ticket?.isGroup === false;
      }

      return true;
    };

    const hasQueueFilter =
      Array.isArray(selectedQueueIds) && selectedQueueIds.length > 0;
    const normalizedSelectedQueueIds = hasQueueFilter
      ? selectedQueueIds.map((queueId) => Number(queueId))
      : [];

    const isQueueSelected = (queueId) => {
      if (queueId === null || queueId === undefined) {
        return false;
      }

      return normalizedSelectedQueueIds.includes(Number(queueId));
    };

    const shouldUpdateTicket = (ticket) =>
      matchesGroupFilter(ticket) &&
      (!ticket.userId || ticket.userId === user?.id || showAll) &&
      (isGroup ||
        !ticket.queueId ||
        !hasQueueFilter ||
        isQueueSelected(ticket.queueId));

    const notBelongsToUserQueues = (ticket) =>
      !isGroup &&
      hasQueueFilter &&
      ticket.queueId &&
      !isQueueSelected(ticket.queueId);

    const handleSocketJoin = () => {
      if (status) {
        socket.emit("joinTickets", status);
      } else {
        socket.emit("joinNotification");
      }
    };

    const ticketEvent = `company-${companyId}-ticket`;
    const appMessageEvent = `company-${companyId}-appMessage`;
    const contactEvent = `company-${companyId}-contact`;

    const handleTicket = (data) => {
      if (data.action === "updateUnread") {
        if (data.userId && Number(data.userId) !== Number(user?.id)) {
          return;
        }
        dispatch({
          type: "RESET_UNREAD",
          payload: data.ticketId,
        });
      }

      if (
        data.action === "update" &&
        shouldUpdateTicket(data.ticket) &&
        data.ticket.status === status
      ) {
        dispatch({
          type: "UPDATE_TICKET",
          payload: data.ticket,
        });
      }

      if (data.action === "update" && notBelongsToUserQueues(data.ticket)) {
        dispatch({ type: "DELETE_TICKET", payload: data.ticket.id });
      }

      if (data.action === "delete") {
        dispatch({ type: "DELETE_TICKET", payload: data.ticketId });
      }
    };

    const handleAppMessage = (data) => {
      const queueIds = queues.map((q) => q.id);
      const dataQueueId = Number(data.ticket?.queue?.id);
      if (
        profile === "user" &&
        !isGroup &&
        (!Number.isFinite(dataQueueId) ||
          queueIds.indexOf(dataQueueId) === -1 ||
          data.ticket.queue === null)
      ) {
        return;
      }

      if (
        data.action === "create" &&
        shouldUpdateTicket(data.ticket) &&
        (status === undefined || data.ticket.status === status)
      ) {
        dispatch({
          type: "UPDATE_TICKET_UNREAD_MESSAGES",
          payload: data.ticket,
        });
      }
    };

    const handleContact = (data) => {
      if (data.action === "update") {
        dispatch({
          type: "UPDATE_TICKET_CONTACT",
          payload: data.contact,
        });
      }
    };
    socket.on("ready", handleSocketJoin);
    socket.on("connect", handleSocketJoin);

    if (socket.connected) {
      handleSocketJoin();
    }

    socket.on(ticketEvent, handleTicket);
    socket.on(appMessageEvent, handleAppMessage);
    socket.on(contactEvent, handleContact);

    return () => {
      socket.off("ready", handleSocketJoin);
      socket.off("connect", handleSocketJoin);
      socket.off(ticketEvent, handleTicket);
      socket.off(appMessageEvent, handleAppMessage);
      socket.off(contactEvent, handleContact);
    };
  }, [
    status,
    showAll,
    user,
    selectedQueueIds,
    tags,
    users,
    profile,
    queues,
    socketManager,
    isGroup,
  ]);

  useEffect(() => {
    if (typeof updateCount === "function") {
      updateCount(ticketsList.length);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketsList]);

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;

    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;

    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  return (
    <Paper className={classes.ticketsListWrapper} style={style}>
      <Paper
        square
        name="closed"
        elevation={0}
        className={classes.ticketsList}
        onScroll={handleScroll}
      >
        <List style={{ paddingTop: 0 }}>
          {ticketsList.length === 0 && !loading ? (
            <div className={classes.noTicketsDiv}>
              <span className={classes.noTicketsTitle}>
                {i18n.t("ticketsList.noTicketsTitle")}
              </span>
              <p className={classes.noTicketsText}>
                {i18n.t("ticketsList.noTicketsMessage")}
              </p>
            </div>
          ) : (
            <>
              {ticketsList.map((ticket) => (
                <TicketListItem
                  ticket={ticket}
                  status={status}
                  isGroup={isGroup}
                  key={ticket.id}
                />
              ))}
            </>
          )}
          {loading && <TicketsListSkeleton />}
        </List>
      </Paper>
    </Paper>
  );
};

export default TicketsListCustom;
